import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowRight, Zap, Route as RouteIcon, Target, Shield, Brain, Trophy, Users, ChevronRight } from "lucide-react";

// Official SCU 7v7 promotional poster
const HERO_IMG = "https://customer-assets-7cd3h4nn.emergentagent.net/job_parish-united/artifacts/sxtrx0tt_7V7.png";

const skills = [
  { icon: Zap, title: "Speed & Agility", body: "Improve movement, quickness, reaction time, and athletic ability." },
  { icon: RouteIcon, title: "Route Running", body: "Develop better routes, timing, spacing, releases, and receiving fundamentals." },
  { icon: Target, title: "Quarterback Development", body: "Improve reads, decision-making, accuracy, timing, and understanding of defensive coverages." },
  { icon: Shield, title: "Defensive Skills", body: "Develop coverage techniques, communication, positioning, and ball skills." },
  { icon: Brain, title: "Football IQ", body: "Help athletes better understand offensive concepts, defensive coverages, spacing, and situational football." },
  { icon: Trophy, title: "Competition", body: "Give athletes opportunities to compete against talented players and teams." },
  { icon: Users, title: "Teamwork & Leadership", body: "Teach communication, accountability, confidence, discipline, and leadership." },
];

const whyPlay = [
  "Get more football repetitions",
  "Improve passing and receiving skills",
  "Develop defensive coverage skills",
  "Increase speed and agility",
  "Learn offensive and defensive concepts",
  "Build confidence",
  "Compete against other athletes",
  "Prepare for tackle football",
  "Continue developing during the offseason",
];

export default function SevenVSeven() {
  return (
    <div data-testid="sevenvseven-page">
      {/* HERO */}
      <section className="relative bg-scu-obsidian text-white overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-scu-obsidian via-scu-purple/25 to-scu-obsidian" />
        <div className="scanlines absolute inset-0 opacity-20" />
        <div className="relative mx-auto max-w-[1400px] px-4 lg:px-8 py-14 md:py-20 grid md:grid-cols-12 gap-8 md:gap-12 items-center">
          {/* Promotional poster — object-contain preserves the graphic exactly as designed, no cropping/stretching on any breakpoint */}
          <div className="md:col-span-7 order-1 flex justify-center">
            <div className="relative w-full max-w-[640px] rounded-md overflow-hidden ring-1 ring-scu-electric/25 scu-purple-glow">
              <img
                src={HERO_IMG}
                alt="St. Charles United 7v7 — Ages 8-12"
                className="block w-full h-auto object-contain"
                loading="eager"
              />
            </div>
          </div>
          {/* Text + CTA column */}
          <div className="md:col-span-5 order-2 text-center md:text-left">
            <div className="text-xs uppercase tracking-[0.35em] text-scu-electric font-bold">Program</div>
            <h1 className="mt-3 font-bebas text-5xl md:text-6xl lg:text-7xl tracking-wider leading-[0.95]">
              St. Charles United<br /><span className="text-scu-electric">7v7</span>
            </h1>
            <div className="mt-4 inline-flex items-center gap-2 bg-scu-purple/25 border border-scu-electric/40 backdrop-blur-md px-3 py-1 rounded-md">
              <span className="text-[10px] uppercase tracking-[0.35em] text-scu-electric font-bold">Ages 8–12</span>
            </div>
            <p className="mt-6 font-bebas text-2xl md:text-3xl tracking-widest text-white/95">Speed. Skill. Competition. Development.</p>
            <div className="mt-8 flex justify-center md:justify-start">
              <Button asChild data-testid="hero-pre-register-7v7" className="bg-scu-purple hover:bg-scu-electric text-white font-bold uppercase tracking-widest h-12 px-6">
                <Link to="/pre-registration?program=7v7">Pre-Register for 7v7 <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </div>
          </div>
        </div>
        <div className="stripe-divider" />
      </section>

      {/* ABOUT SCU 7v7 */}
      <section className="mx-auto max-w-[1400px] px-4 lg:px-8 py-20" data-testid="about-7v7">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="text-xs uppercase tracking-[0.3em] text-scu-purple font-bold">About</div>
            <h2 className="mt-3 font-bebas text-5xl md:text-6xl tracking-wide text-scu-obsidian leading-none">About<br />SCU 7v7</h2>
            <div className="mt-6 h-1 w-20 bg-scu-purple" />
          </div>
          <div className="md:col-span-7 text-scu-smoke text-lg leading-relaxed space-y-5">
            <p>
              St. Charles United 7v7 is designed to give young athletes another opportunity to develop their football
              skills in a fast-paced and competitive environment. Our program will focus on improving passing, receiving,
              coverage, route running, communication, football IQ, speed, and overall athletic development.
            </p>
            <p>
              7v7 gives athletes the opportunity to get more repetitions, compete, build confidence, and continue
              developing their skills outside of traditional tackle football.
            </p>
            <p>
              SCU's goal is not simply to win games. We want to develop better football players, better teammates, and
              confident young athletes who are prepared for their next level of competition.
            </p>
          </div>
        </div>
      </section>

      {/* WHAT ATHLETES WILL DEVELOP */}
      <section className="bg-scu-off border-y border-scu-smoke/15 py-20" data-testid="skills-section">
        <div className="mx-auto max-w-[1400px] px-4 lg:px-8">
          <div className="text-xs uppercase tracking-[0.3em] text-scu-purple font-bold">Development</div>
          <h2 className="mt-3 font-bebas text-5xl md:text-6xl tracking-wide text-scu-obsidian">What Athletes Will Develop</h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {skills.map((s) => (
              <Card key={s.title} data-testid={`skill-${s.title.toLowerCase().replace(/\s+/g,'-').replace(/&/g,'and')}`} className="border-scu-smoke/20 rounded-md p-6 hover:border-scu-purple hover:-translate-y-1 transition-all bg-white">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-md bg-scu-purple/10 flex items-center justify-center">
                    <s.icon className="h-5 w-5 text-scu-purple" />
                  </div>
                  <div className="font-bebas text-2xl tracking-wide text-scu-obsidian">{s.title}</div>
                </div>
                <p className="mt-3 text-scu-smoke">{s.body}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* WHO CAN PARTICIPATE */}
      <section className="mx-auto max-w-[1400px] px-4 lg:px-8 py-20" data-testid="who-can-participate">
        <div className="grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6 relative overflow-hidden rounded-md bg-scu-purple text-white p-10 md:p-14 scu-purple-glow">
            <div className="scanlines absolute inset-0 opacity-30" />
            <div className="relative">
              <div className="text-[10px] uppercase tracking-[0.35em] text-white/80 font-bold">Who Can Participate?</div>
              <div className="mt-4 font-bebas text-8xl md:text-9xl tracking-wide leading-none">Ages 8–12</div>
              <div className="mt-4 text-white/90">SCU 7v7 will be available to athletes ages 8, 9, 10, 11, and 12.</div>
            </div>
          </div>
          <div className="md:col-span-6 text-scu-smoke text-lg leading-relaxed space-y-4">
            <p>
              Our 7v7 program is age-restricted so athletes compete against peers at similar developmental stages.
              Every 7v7 pre-registration is validated to ensure only athletes ages <b className="text-scu-obsidian">8–12</b> can be enrolled.
            </p>
            <p>
              Athletes younger than 8 or older than 12 will not be eligible for 7v7 at this time — but our Football and Cheer
              programs offer additional options for other ages.
            </p>
            <div className="pt-3 flex flex-wrap gap-3">
              <Button asChild variant="outline" className="border-scu-smoke/30 text-scu-obsidian hover:bg-scu-obsidian hover:text-white font-bold uppercase tracking-widest">
                <Link to="/football">Football <ChevronRight className="ml-1 h-4 w-4" /></Link>
              </Button>
              <Button asChild variant="outline" className="border-scu-smoke/30 text-scu-obsidian hover:bg-scu-obsidian hover:text-white font-bold uppercase tracking-widest">
                <Link to="/cheer">Cheer <ChevronRight className="ml-1 h-4 w-4" /></Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* WHY PLAY 7v7 */}
      <section className="bg-scu-obsidian text-white py-20 relative overflow-hidden" data-testid="why-play-7v7">
        <div className="scanlines absolute inset-0 opacity-25" />
        <div className="mx-auto max-w-[1400px] px-4 lg:px-8 relative">
          <div className="text-xs uppercase tracking-[0.3em] text-scu-electric font-bold">Why 7v7</div>
          <h2 className="mt-3 font-bebas text-5xl md:text-6xl tracking-wide">Why Play 7v7?</h2>
          <p className="mt-4 text-white/70 max-w-3xl text-lg">
            7v7 provides athletes with additional opportunities to sharpen every part of their game.
          </p>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {whyPlay.map((w, idx) => (
              <div key={w} className="flex items-start gap-3 border border-white/10 bg-white/5 backdrop-blur-md rounded-md p-5 hover:border-scu-electric transition-colors">
                <span className="font-bebas text-2xl text-scu-electric leading-none">{String(idx + 1).padStart(2, "0")}</span>
                <span className="text-white/90 text-base leading-relaxed">{w}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROGRAM CONNECTION */}
      <section className="mx-auto max-w-[1400px] px-4 lg:px-8 py-20" data-testid="program-connection">
        <div className="text-xs uppercase tracking-[0.3em] text-scu-purple font-bold">The SCU Family</div>
        <h2 className="mt-3 font-bebas text-5xl md:text-6xl tracking-wide text-scu-obsidian">Now Three Programs. One Utes Family.</h2>
        <p className="mt-4 max-w-3xl text-scu-smoke text-lg leading-relaxed">
          7v7 is now part of the St. Charles United athletic program alongside Football and Cheer.
          Each program shares the same standard of coaching, development, and community — building athletes and character across every SCU roster.
        </p>
        <div className="mt-10 grid sm:grid-cols-3 gap-4">
          {[
            { to: "/football", label: "Football", accent: "text-scu-purple" },
            { to: "/cheer", label: "Cheer", accent: "text-scu-purple" },
            { to: "/7v7", label: "7v7", accent: "text-scu-electric" },
          ].map((p) => (
            <Link key={p.to} to={p.to} className="group border border-scu-smoke/20 bg-white rounded-md p-8 text-center hover:border-scu-purple hover:-translate-y-1 transition-all">
              <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-scu-smoke">SCU Program</div>
              <div className={`mt-2 font-bebas text-5xl tracking-wide ${p.accent}`}>{p.label}</div>
              <div className="mt-3 inline-flex items-center gap-1 text-xs uppercase tracking-widest font-bold text-scu-obsidian group-hover:text-scu-purple">Explore <ChevronRight className="h-4 w-4" /></div>
            </Link>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-scu-purple text-white relative overflow-hidden" data-testid="final-cta">
        <div className="scanlines absolute inset-0 opacity-30" />
        <div className="mx-auto max-w-[1400px] px-4 lg:px-8 py-20 relative">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.35em] text-white/80 font-bold">Ready to Compete?</div>
            <h2 className="mt-3 font-bebas text-6xl md:text-7xl tracking-wide leading-none">Ready to Compete?</h2>
            <p className="mt-4 font-bebas text-3xl md:text-4xl tracking-widest text-white/95">Join St. Charles United 7v7</p>
            <p className="mt-1 text-sm uppercase tracking-[0.35em] text-white/80 font-bold">Ages 8–12</p>
            <div className="mt-10">
              <Button asChild data-testid="final-pre-register-7v7" className="bg-white text-scu-purple hover:bg-scu-obsidian hover:text-white font-bold uppercase tracking-widest h-14 px-8 rounded-md text-base">
                <Link to="/pre-registration?program=7v7">Pre-Register Now <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </div>
            <p className="mt-6 text-xs text-white/80 max-w-xl">
              Pre-registration is for interest and planning purposes and is not a commitment to participate.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
